import React, { FC, useState, useRef } from 'react';
import { styled } from '@mui/system';
import { colors, sizes } from 'src/themes/default';
import translate from '@translate';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Typography from '../Typography';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { FileType } from 'src/types/file';
import FileDisplay from '../FileDisplay';
import { FilesPropType } from '.';

export type OnUploadType = (value: File[]) => void;

export interface DroppzonePropsType {
    accept?: string;
    name?: string;
    imageFile?: FileType;
    multiple?: boolean;
    disabled?: boolean;
    required?: boolean;
    image?: boolean;
    imageCircle?: boolean;
    displayFilesList?: boolean;
    height: number;
    onUpload: OnUploadType;
    onChange?: (value: File[] | null) => void;
    onDeleteImage: () => void;
    files?: FilesPropType;
}

const DropzoneImageContainer = styled('div')(({ theme }) => ({
    display: 'block',
    verticalAlign: 'top',
    textAlign: 'center',

    '&.hightlight > div': {
        borderColor: colors.primary_O,
        color: colors.primary_O,
    },
    '&.hightlight p': {
        color: colors.primary_O,
    },

    [theme.breakpoints.down('sm')]: {
        display: 'block',
    },
}));

const DropzoneImage = styled('div')({
    margin: 'auto',
    position: 'relative',
    cursor: 'pointer',

    '& svg, & .file-upload-add-photo': {
        position: 'absolute',
        borderRadius: sizes.file_input_svg_R,
        padding: '4px',
        color: colors.primary_W,
        height: sizes.file_input_svg_height,
        width: sizes.file_input_svg_height,
    },

    '& svg.file-upload-add-photo': {
        borderRadius: 0,
        color: colors.primary_W,
        fill: colors.primary_W,
    },
    '& div.file-upload-add-photo': {
        borderRadius: sizes.file_input_svg_R,
        background: colors.primary_O,
    },

    '& svg.file-upload-remove-photo': {
        padding: '1px',
        borderRadius: sizes.file_input_svg_R,
        background: colors.primary_W,
        color: colors.primary_O,
        border: '1px solid silver',
    },
});

const DropzoneImageBorder = styled('div')({
    border: '1px dashed ' + colors.avatar_border,
    textAlign: 'center',
    verticalAlign: 'top',
    backgroundColor: colors['background_B-1'],
    margin: 'auto',
    overflow: 'hidden',
    position: 'relative',
});

const DropzoneContainer = styled('div')(({ theme }) => ({
    cursor: 'pointer',
    display: 'inline-block',
    verticalAlign: 'top',
    textAlign: 'center',

    '&.disabled': {
        cursor: 'no-drop',
    },

    '&.hightlight > div': {
        borderColor: colors.primary_O,
        color: colors.primary_O,
    },
    '&.hightlight p': {
        color: colors.primary_O,
    },

    [theme.breakpoints.down('sm')]: {
        display: 'block',
    },
}));

const DropzoneDefault = styled('div')(({ theme }) => ({
    height: '100%',
    width: sizes.file_input_W,
    border: '1px dashed ' + colors.avatar_border,
    textAlign: 'center',
    verticalAlign: 'top',
    backgroundColor: colors['background_B-1'],
    position: 'relative',

    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },

    '& > div': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        width: '100%',
    },
}));

const Droppzone: FC<DroppzonePropsType> = (props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [hightlight, setHightlight] = useState<boolean>(false);

    const addFiles = (files: FileList | null) => {
        const newFiles = Array.from(files || []);
        props.onUpload(newFiles || []);
    };

    const onUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (props.disabled) {
            return;
        }

        addFiles(event.target.files);
    };

    const onDragOver = (event: React.DragEvent<HTMLInputElement>) => {
        event.preventDefault();
        if (props.disabled) return;
        setHightlight(true);
    };

    const onDragLeave = () => {
        setHightlight(false);
    };

    const onDrop = (event: React.DragEvent<HTMLInputElement>) => {
        event.preventDefault();
        if (props.disabled) return;
        addFiles(event.dataTransfer.files);
        setHightlight(false);
    };

    const openFileDialog = () => {
        if (props.disabled) {
            return;
        }
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const hiddenInput = (
        <input
            ref={fileInputRef}
            id="file"
            style={{ display: 'none' }}
            type="file"
            multiple={props.multiple}
            onChange={onUpload}
            accept={props.accept}
            required={props.required && !props.files?.length}
            name={props.name}
            onClick={() => {
                if (fileInputRef && fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }}
        />
    );

    const containerSX: any = {};
    if (!props.displayFilesList) {
        containerSX.width = '100%';
    }

    if (props.image) {
        let addImgPosition = 5;
        const sx: any = {};

        sx.height = props.height;
        sx.width = props.height;

        if (props.imageCircle) {
            sx.borderRadius = props.height / 2;
            addImgPosition = Math.floor((props.height - 52) * 0.1);
        }

        return (
            <DropzoneImageContainer>
                <DropzoneImage
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onClick={props.imageFile ? undefined : openFileDialog}
                    className={hightlight ? 'hightlight' : ''}
                    sx={sx}
                >
                    <DropzoneImageBorder sx={sx}>
                        {props.imageFile ? (
                            <FileDisplay file={props.imageFile} disableModal />
                        ) : (
                            <Typography variant="body1" sx={{ paddingTop: Math.floor((props.height - 18) / 2) + 'px' }}>
                                {translate('fileUpload.dowloadImage')}
                            </Typography>
                        )}
                    </DropzoneImageBorder>
                    <div
                        className="file-upload-add-photo"
                        style={{
                            bottom: addImgPosition,
                            right: addImgPosition,
                        }}
                    />
                    <AddAPhotoIcon
                        className="file-upload-add-photo"
                        sx={{
                            bottom: addImgPosition,
                            right: addImgPosition,
                        }}
                        onClick={props.imageFile ? openFileDialog : undefined}
                    />
                    {props.imageFile && (
                        <HighlightOffIcon
                            className="file-upload-remove-photo"
                            onClick={props.onDeleteImage}
                            sx={{
                                top: addImgPosition,
                                left: addImgPosition,
                            }}
                        />
                    )}
                </DropzoneImage>
                {hiddenInput}
            </DropzoneImageContainer>
        );
    }

    return (
        <DropzoneContainer
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={openFileDialog}
            className={`${hightlight ? 'hightlight' : ''} ${props.disabled ? 'disabled' : ''}`}
            sx={containerSX}
        >
            <DropzoneDefault sx={{ minHeight: props.height }}>
                <div>
                    <CloudUploadIcon />
                    <Typography variant="body1">{translate('fileUpload.downloadFiles')}</Typography>
                </div>
            </DropzoneDefault>
            {hiddenInput}
        </DropzoneContainer>
    );
};

export default Droppzone;
